import React, { useState } from "react";
import InfoTip from "../common/InfoTip";
import { useQuery, useMutation, gql } from "@apollo/client";
import { GET_EMPLOYEES } from "./leaveShared";

// Leave policies: the single source of truth for leave entitlement.
// One generic policy per leave type applies to everyone; an employee can
// have their own copy of a type's policy, which replaces the generic one
// for them (e.g. Casual Leave with 2 days a month for one senior stylist).

export const GET_LEAVE_POLICIES = gql`
  query GetLeavePolicies {
    getLeavePolicies {
      status
      message
      policies {
        id
        leaveType
        employeeId
        employeeName
        label
        isActive
        isPaid
        balancePool
        accrualFrequency
        accrualAmount
        carryForwardCap
        noticeDays
        maxDaysPerRequest
        requiresDocument
        documentRequiredAfterDays
      }
    }
  }
`;

const UPSERT_POLICY = gql`
  mutation UpsertLeavePolicy($data: LeavePolicyInput!) {
    upsertLeavePolicy(data: $data) {
      status
      message
    }
  }
`;

const DELETE_EMPLOYEE_POLICY = gql`
  mutation DeleteEmployeeLeavePolicy($id: ID!) {
    deleteEmployeeLeavePolicy(id: $id) {
      status
      message
    }
  }
`;

const EMPTY = {
  leaveType: "",
  employeeId: "",
  label: "",
  isActive: true,
  isPaid: true,
  balancePool: "",
  accrualFrequency: "MONTHLY",
  accrualAmount: 0,
  carryForwardCap: "",
  noticeDays: "",
  maxDaysPerRequest: "",
  requiresDocument: false,
  documentRequiredAfterDays: 0,
};

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const describeAccrual = (p) => {
  if (p.balancePool !== p.leaveType) return `Uses ${p.balancePool} balance`;
  if (p.accrualFrequency === "NONE") return "Manual credits only";
  const carry =
    p.carryForwardCap === null
      ? "all carries forward"
      : p.carryForwardCap === 0
        ? "nothing carries forward"
        : `up to ${p.carryForwardCap} carries forward`;
  return `${p.accrualAmount} day${p.accrualAmount === 1 ? "" : "s"} per ${
    p.accrualFrequency === "YEARLY" ? "year" : "month"
  }, ${carry}`;
};

const describeDocument = (p) =>
  p.requiresDocument
    ? p.documentRequiredAfterDays
      ? `If over ${p.documentRequiredAfterDays} days`
      : "Always"
    : "No";

// What each policy setting means - shown as ⓘ in the table and the form
export const POLICY_HELP = {
  active:
    "Off = employees can't pick this leave type in the app. Admins can still record it.",
  paid:
    "Paid: a day of this leave uses 1 day from the balance instead of cutting a day's salary - on any day, weekday, Saturday, Sunday or restricted day - while the balance lasts. When the balance is used up, or during probation, the day's salary is cut like unpaid leave. The leave fine (advance / short-notice / weekend / restricted) applies either way. An absent day never uses the balance. Unpaid: every day of this leave cuts a day's salary.",
  balance:
    "The balance a paid day uses up. A type can have its own balance or share another's - e.g. Sick using Casual's days.",
  accrues:
    "How often days are added to the balance: every month (on the 1st), every year (1 Jan), or never - then only admins add days (e.g. comp-off) from Balances.",
  credited: "How many days are added to the balance each time it accrues.",
  carry:
    "Unused days that move to the next month / year. 0 = unused days lapse at the end of each period; empty = everything carries forward.",
  notice:
    "How many days before the leave starts it must be applied to count as planned (advance leave, lower fine). Applied later = short-notice: the short-notice fine instead of the advance fine, and it counts as unplanned in reports. Empty = the global 'Advance notice needed' setting.",
  maxDays: "The longest one application can be from the app. Admins can still record longer. Empty = no limit.",
  document:
    "Ask for a document (e.g. a medical note) when the leave is longer than the given days. Employees attach it from the app; missing ones show in Staff Desk.",
};

const Field = ({ label, hint, children, info }) => (
  <label className="block">
    <span className="block text-sm font-medium mb-1">
      {label}
      {info && <InfoTip title={label} text={info} />}
    </span>
    {children}
    {hint && <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>}
  </label>
);

const PolicyCells = ({ p }) => (
  <>
    <td className="py-2 px-3">{p.isPaid ? "Paid" : "Unpaid"}</td>
    <td className="py-2 px-3">{p.isPaid ? describeAccrual(p) : "-"}</td>
    <td className="py-2 px-3">
      {p.noticeDays ?? "default"} day notice
      {p.maxDaysPerRequest ? ` · max ${p.maxDaysPerRequest} days` : ""}
    </td>
    <td className="py-2 px-3">{describeDocument(p)}</td>
  </>
);

const LeavePolicies = () => {
  const { data, loading, refetch } = useQuery(GET_LEAVE_POLICIES, {
    fetchPolicy: "network-only",
  });
  const { data: empData } = useQuery(GET_EMPLOYEES, {
    variables: { data: {} },
    fetchPolicy: "cache-first",
  });
  const [upsert] = useMutation(UPSERT_POLICY);
  const [removeEmployeePolicy] = useMutation(DELETE_EMPLOYEE_POLICY);
  const [form, setForm] = useState(null);
  const [mode, setMode] = useState("edit"); // "new-type" | "edit" | "new-employee"
  const [error, setError] = useState("");

  const all = data?.getLeavePolicies?.policies || [];
  const generic = all.filter((p) => !p.employeeId);
  const forEmployees = (type) => all.filter((p) => p.employeeId && p.leaveType === type);
  const ownPools = generic.filter((p) => p.balancePool === p.leaveType);
  const employees = empData?.getEmployeelist?.employee || [];

  const asForm = (p) => ({
    ...p,
    employeeId: p.employeeId || "",
    carryForwardCap: p.carryForwardCap ?? "",
    noticeDays: p.noticeDays ?? "",
    maxDaysPerRequest: p.maxDaysPerRequest ?? "",
  });

  const open = (nextMode, policy) => {
    setError("");
    setMode(nextMode);
    if (nextMode === "new-type") setForm({ ...EMPTY });
    // An employee's policy starts as a copy of the generic one
    else if (nextMode === "new-employee") setForm({ ...asForm(policy), id: undefined, employeeId: "" });
    else setForm(asForm(policy));
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async (e) => {
    e.preventDefault();
    setError("");
    const leaveType = form.leaveType.trim().toUpperCase();
    try {
      const res = await upsert({
        variables: {
          data: {
            leaveType,
            employeeId: form.employeeId || null,
            label: form.label || leaveType,
            isActive: form.isActive,
            isPaid: form.isPaid,
            ...(form.employeeId ? {} : { balancePool: form.balancePool || leaveType }),
            accrualFrequency: form.accrualFrequency,
            accrualAmount: Number(form.accrualAmount) || 0,
            carryForwardCap: numOrNull(form.carryForwardCap),
            noticeDays: numOrNull(form.noticeDays),
            maxDaysPerRequest: numOrNull(form.maxDaysPerRequest),
            requiresDocument: form.requiresDocument,
            documentRequiredAfterDays: Number(form.documentRequiredAfterDays) || 0,
          },
        },
      });
      const result = res.data?.upsertLeavePolicy;
      if (result?.status !== "SUCCESS") throw new Error(result?.message || "Save failed");
      setForm(null);
      refetch();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.employeeName}'s own ${p.label} policy? The generic one will apply to them.`)) return;
    const res = await removeEmployeePolicy({ variables: { id: p.id } });
    const result = res.data?.deleteEmployeeLeavePolicy;
    if (result?.status !== "SUCCESS") alert(result?.message || "Failed");
    refetch();
  };

  const isEmployeePolicy = Boolean(form?.employeeId) || mode === "new-employee";
  const ownsPool =
    form && (isEmployeePolicy ? form.balancePool : form.balancePool || form.leaveType.toUpperCase()) ===
      form.leaveType.toUpperCase();
  // Employees who already have their own policy for this type
  const taken = new Set(form ? forEmployees(form.leaveType).map((p) => p.employeeId) : []);

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-gray-600">
          The generic policy applies to everyone. Give one employee different terms with
          "+ For an employee"; their policy replaces the generic one for them. A paid day only skips
          the day's salary charge - fines are unchanged.
        </p>
        <button
          onClick={() => open("new-type")}
          className="shrink-0 ml-4 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
        >
          + Leave type
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <table className="min-w-full text-left text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="py-2 px-3">Type / applies to</th>
              <th className="py-2 px-3">
                Paid
                <InfoTip title="Paid" text={POLICY_HELP.paid} />
              </th>
              <th className="py-2 px-3">
                Balance
                <InfoTip title="Balance" text={`${POLICY_HELP.balance} ${POLICY_HELP.accrues} ${POLICY_HELP.carry}`} />
              </th>
              <th className="py-2 px-3">
                Notice / max
                <InfoTip title="Notice days" text={`${POLICY_HELP.notice} Max: ${POLICY_HELP.maxDays}`} />
              </th>
              <th className="py-2 px-3">
                Document
                <InfoTip title="Document" text={POLICY_HELP.document} align="right" />
              </th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {generic.map((p) => (
              <React.Fragment key={p.id}>
                <tr className={`border-b ${p.isActive ? "" : "opacity-50"}`}>
                  <td className="py-2 px-3">
                    <div className="font-medium text-gray-800">{p.label}</div>
                    <div className="text-xs text-gray-500">
                      {p.leaveType} · everyone
                      {!p.isActive && " · inactive"}
                    </div>
                  </td>
                  <PolicyCells p={p} />
                  <td className="py-2 px-3 text-right whitespace-nowrap space-x-2">
                    <button
                      onClick={() => open("new-employee", p)}
                      className="text-xs px-3 py-1 border rounded bg-white hover:bg-gray-100"
                    >
                      + For an employee
                    </button>
                    <button
                      onClick={() => open("edit", p)}
                      className="text-xs px-3 py-1 border rounded bg-white hover:bg-gray-100"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
                {forEmployees(p.leaveType).map((ep) => (
                  <tr key={ep.id} className={`border-b bg-blue-50/40 ${ep.isActive ? "" : "opacity-50"}`}>
                    <td className="py-2 px-3 pl-8">
                      <div className="text-gray-800">↳ {ep.employeeName || ep.employeeId}</div>
                      <div className="text-xs text-gray-500">
                        own {ep.label} policy{!ep.isActive && " · inactive"}
                      </div>
                    </td>
                    <PolicyCells p={ep} />
                    <td className="py-2 px-3 text-right whitespace-nowrap space-x-2">
                      <button
                        onClick={() => open("edit", ep)}
                        className="text-xs px-3 py-1 border rounded bg-white hover:bg-gray-100"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => remove(ep)}
                        className="text-xs px-3 py-1 border rounded bg-white text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <form
            onSubmit={save}
            className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto space-y-4"
          >
            <h2 className="text-lg font-semibold text-gray-800">
              {mode === "new-type"
                ? "New leave type"
                : mode === "new-employee"
                  ? `${form.label} for one employee`
                  : form.employeeId
                    ? `${form.employeeName}'s ${form.label}`
                    : `Edit ${form.label} (everyone)`}
            </h2>

            {mode === "new-employee" && (
              <Field label="Employee" hint="Starts as a copy of the generic policy - change what differs">
                <select
                  required
                  className="w-full border p-2 rounded bg-white"
                  value={form.employeeId}
                  onChange={(e) => set("employeeId", e.target.value)}
                >
                  <option value="" disabled>
                    Select employee
                  </option>
                  {employees
                    .filter((emp) => !taken.has(emp.id))
                    .map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.fullName}
                      </option>
                    ))}
                </select>
              </Field>
            )}

            {!isEmployeePolicy && (
              <div className="grid grid-cols-2 gap-4">
                <Field label="Type code" hint="Letters and underscores, e.g. MATERNITY">
                  <input
                    required
                    disabled={mode !== "new-type"}
                    className="w-full border p-2 rounded disabled:bg-gray-100"
                    value={form.leaveType}
                    onChange={(e) => set("leaveType", e.target.value.toUpperCase())}
                  />
                </Field>
                <Field label="Name shown to employees">
                  <input
                    required
                    className="w-full border p-2 rounded"
                    value={form.label}
                    onChange={(e) => set("label", e.target.value)}
                  />
                </Field>
              </div>
            )}

            <div className="flex gap-6 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                />
                {isEmployeePolicy ? "Can apply for this type" : "Employees can apply"}
                <InfoTip title="Can apply" text={POLICY_HELP.active} />
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isPaid}
                  onChange={(e) => set("isPaid", e.target.checked)}
                />
                Paid (spends a balance)
                <InfoTip title="Paid" text={POLICY_HELP.paid} />
              </label>
            </div>

            {form.isPaid && (
              <>
                {isEmployeePolicy ? (
                  <p className="text-xs text-gray-500">
                    Spends the {form.balancePool} balance, same as the generic policy.
                  </p>
                ) : (
                  <Field
                    label="Spends balance of"
                    info={POLICY_HELP.balance}
                    hint="Its own balance, or share another type's (e.g. Sick from Casual)"
                  >
                    <select
                      className="w-full border p-2 rounded bg-white"
                      value={form.balancePool || form.leaveType}
                      onChange={(e) => set("balancePool", e.target.value)}
                    >
                      <option value={form.leaveType}>Its own ({form.leaveType || "new"})</option>
                      {ownPools
                        .filter((p) => p.leaveType !== form.leaveType)
                        .map((p) => (
                          <option key={p.leaveType} value={p.leaveType}>
                            {p.label} ({p.leaveType})
                          </option>
                        ))}
                    </select>
                  </Field>
                )}

                {ownsPool && (
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Accrues" info={POLICY_HELP.accrues}>
                      <select
                        className="w-full border p-2 rounded bg-white"
                        value={form.accrualFrequency}
                        onChange={(e) => set("accrualFrequency", e.target.value)}
                      >
                        <option value="MONTHLY">Every month</option>
                        <option value="YEARLY">Every year (1 Jan)</option>
                        <option value="NONE">Never (manual credits)</option>
                      </select>
                    </Field>
                    {form.accrualFrequency !== "NONE" && (
                      <Field label="Days credited each period" info={POLICY_HELP.credited}>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          className="w-full border p-2 rounded"
                          value={form.accrualAmount}
                          onChange={(e) => set("accrualAmount", e.target.value)}
                        />
                      </Field>
                    )}
                    {form.accrualFrequency !== "NONE" && (
                      <Field
                        label="Carry-forward cap (days)"
                        info={POLICY_HELP.carry}
                        hint="0 = resets each period; empty = no limit"
                      >
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          className="w-full border p-2 rounded"
                          value={form.carryForwardCap}
                          onChange={(e) => set("carryForwardCap", e.target.value)}
                        />
                      </Field>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Notice days"
                info={POLICY_HELP.notice}
                hint="Below this it counts as emergency; empty = global setting"
              >
                <input
                  type="number"
                  min="0"
                  className="w-full border p-2 rounded"
                  value={form.noticeDays}
                  onChange={(e) => set("noticeDays", e.target.value)}
                />
              </Field>
              <Field label="Max days per request" info={POLICY_HELP.maxDays} hint="Empty = no limit">
                <input
                  type="number"
                  min="1"
                  className="w-full border p-2 rounded"
                  value={form.maxDaysPerRequest}
                  onChange={(e) => set("maxDaysPerRequest", e.target.value)}
                />
              </Field>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.requiresDocument}
                  onChange={(e) => set("requiresDocument", e.target.checked)}
                />
                Ask for a document
                <InfoTip title="Document" text={POLICY_HELP.document} />
              </label>
              {form.requiresDocument && (
                <label className="flex items-center gap-2">
                  when longer than
                  <input
                    type="number"
                    min="0"
                    className="w-16 border p-1 rounded"
                    value={form.documentRequiredAfterDays}
                    onChange={(e) => set("documentRequiredAfterDays", e.target.value)}
                  />
                  days
                </label>
              )}
            </div>

            {mode === "edit" && !form.employeeId && forEmployees(form.leaveType).length > 0 && (
              <p className="text-xs text-amber-700">
                {forEmployees(form.leaveType).length} employee(s) have their own {form.label} policy;
                changes here don't apply to them.
              </p>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setForm(null)}
                className="px-4 py-2 border rounded bg-white"
              >
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white">
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default LeavePolicies;
